// THE PHONE LINE (Stage 4.8f.4a, guide §6.12) — the browser's SIP line for the new phone: registers the person's extension over
// WebSocket (JsSIP), and holds every call they are on — more than one, for CALL WAITING (the owner's F3): a second call rings while
// they talk; "hold and answer" puts the first on hold, one tap switches back. The study's bugs, fixed here:
//   · a second INVITE no longer overwrites the call in progress (each call is its own line; a third is answered "busy");
//   · the caller's display name is kept through answering;
//   · the microphone is checked when the phone opens, not when a customer is already ringing;
//   · stopping unregisters first (the extension is not left "registered" to a closed tab);
//   · calls are not kept in memory here — the recent list is the server's (/api/phone/recent).
// Transfers are the engine's (4.8f.2, phoneApi.js); this line only rings, talks, holds, mutes and sends keypad tones.
import { useCallback, useEffect, useRef, useState } from "react";

const SIP_WS     = import.meta.env.VITE_SIP_WS_URL;
const SIP_DOMAIN = import.meta.env.VITE_SIP_DOMAIN;
const ICE        = [{ urls: "stun:stun.l.google.com:19302" }];
const MEDIA      = { audio: true, video: false };
const sipLog = (...a) => { if (import.meta.env.DEV) console.log(...a); };

// The one UA of this tab, so signing out can unregister before the session is cleared (SideBar.jsx).
let currentUa = null;
export async function unregisterSIP() {
  const ua = currentUa;
  if (!ua) return;
  currentUa = null;
  try {
    if (ua.isRegistered()) {
      await new Promise((resolve) => { ua.once("unregistered", resolve); ua.unregister({ all: true }); setTimeout(resolve, 800); });
    }
  } catch { /* closing anyway */ }
  try { ua.stop(); } catch { /* already stopped */ }
}

// Two short tones every 2.6 s while a call rings with nothing else going on; one soft tone every 3 s while a second call waits.
function startTones(kind) {
  let ctx;
  try { ctx = new (window.AudioContext || window.webkitAudioContext)(); } catch { return () => {}; }
  const beep = (at, freq, vol) => {
    try {
      const osc = ctx.createOscillator(), gain = ctx.createGain();
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(vol, at + 0.05);
      gain.gain.setValueAtTime(vol, at + 0.38);
      gain.gain.linearRampToValueAtTime(0, at + 0.45);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(at); osc.stop(at + 0.45);
    } catch { /* a closed context */ }
  };
  const once = () => {
    if (ctx.state === "closed") return;
    const now = ctx.currentTime;
    if (kind === "waiting") beep(now, 520, 0.12);
    else { beep(now, 440, 0.35); beep(now + 0.5, 440, 0.35); }
  };
  let timer = null;
  ctx.resume().then(() => { once(); timer = setInterval(once, kind === "waiting" ? 3000 : 2600); })
    .catch(() => console.warn("[phone] ring tone blocked — no click on this page yet"));
  return () => { clearInterval(timer); if (ctx.state !== "closed") ctx.close().catch(() => {}); };
}

let seq = 0;

export default function usePhoneLine({ extension, password, enabled = true, onFocusChange, onCallEnded, notifyText }) {
  const [reg, setReg]     = useState("idle");      // idle | connecting | registered | reconnecting | failed
  const [regError, setRegError] = useState("");
  const [mic, setMic]     = useState("unknown");   // unknown | ok | blocked | none
  const [calls, setCalls] = useState([]);          // [{ id, dir, number, name, state, since, answeredAt, muted }]
  const uaRef     = useRef(null);
  const linesRef  = useRef(new Map());             // id → { session, stream, info }
  const audioRef  = useRef(null);
  const focusRef  = useRef(null);
  const cbRef     = useRef({ onFocusChange, onCallEnded, notifyText });
  useEffect(() => { cbRef.current = { onFocusChange, onCallEnded, notifyText }; }, [onFocusChange, onCallEnded, notifyText]);

  const publish = useCallback(() => {
    setCalls([...linesRef.current.values()].map((l) => ({ ...l.info })));
  }, []);

  // The call in focus: the one being talked on, else the one ringing / dialling with nothing else going on. The dashboard's CRM panel
  // follows it (a second call waiting is not shown there until it is answered).
  const reportFocus = useCallback(() => {
    const all = [...linesRef.current.values()].map((l) => l.info);
    const active = all.find((c) => c.state === "active");
    const lone = all.length === 1 ? all[0] : null;
    const f = active ?? (lone && lone.state !== "held" ? lone : null) ?? all.find((c) => c.state === "held") ?? null;
    const state = f ? (f.state === "held" ? "active" : f.state) : "ended";
    const key = f ? `${f.id}:${state}` : "none";
    if (focusRef.current === key) return;
    const prevId = focusRef.current && focusRef.current !== "none" ? focusRef.current.split(":")[0] : null;
    focusRef.current = key;
    const tell = () => cbRef.current.onFocusChange?.(f ? { active: true, callerId: f.number, callerDisplayName: f.name ?? "", state } : { active: false, state: "ended" });
    // Another caller now: the panels of the last one are cleared first (the dashboard loads this caller's from scratch).
    if (f && prevId && prevId !== f.id) { cbRef.current.onFocusChange?.({ active: false, state: "ended" }); setTimeout(tell, 0); }
    else tell();
  }, []);

  // The audio of the call being talked on.
  const playActive = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    const act = [...linesRef.current.values()].find((l) => l.info.state === "active" || l.info.state === "calling");
    const stream = act?.stream ?? null;
    if (el.srcObject !== stream) {
      el.srcObject = stream;
      if (stream) el.play().catch((e) => console.error("[phone] audio play failed:", e));
    }
  }, []);

  const update = useCallback((id, patch) => {
    const l = linesRef.current.get(id);
    if (!l) return;
    l.info = { ...l.info, ...patch };
    publish(); reportFocus(); playActive();
  }, [publish, reportFocus, playActive]);

  const drop = useCallback((id, status) => {
    const l = linesRef.current.get(id);
    if (!l) return;
    linesRef.current.delete(id);
    const durationSec = l.info.answeredAt ? Math.round((Date.now() - l.info.answeredAt) / 1000) : 0;
    publish(); reportFocus(); playActive();
    cbRef.current.onCallEnded?.({ caller: l.info.number, duration: durationSec, status: status ?? (l.info.answeredAt ? "ended" : "missed"), direction: l.info.dir });
  }, [publish, reportFocus, playActive]);

  const watch = useCallback((id, session) => {
    const onTrack = (conn) => conn?.addEventListener("track", (e) => {
      if (!e.streams[0]) return;
      const cur = linesRef.current.get(id);
      if (cur) { cur.stream = e.streams[0]; playActive(); }
    });
    session.on("peerconnection", (d) => onTrack(d.peerconnection));
    if (session.connection) onTrack(session.connection);
    session.on("confirmed", () => update(id, { state: "active", answeredAt: linesRef.current.get(id)?.info.answeredAt ?? Date.now() }));
    session.on("hold",   (e) => { if (e.originator === "local") update(id, { state: "held" }); });
    session.on("unhold", (e) => { if (e.originator === "local") update(id, { state: "active" }); });
    session.on("ended",  () => drop(id, "ended"));
    session.on("failed", (e) => drop(id, session.direction === "incoming" && !linesRef.current.get(id)?.info.answeredAt ? "missed" : (e?.cause === "Canceled" ? "cancelled" : "failed")));
  }, [drop, update, playActive]);

  // ── The microphone, checked when the phone opens ─────────────────────────
  const checkMic = useCallback(async (ask = false) => {
    if (!navigator.mediaDevices?.getUserMedia) { setMic("none"); return "none"; }
    try {
      if (!ask && navigator.permissions?.query) {
        const p = await navigator.permissions.query({ name: "microphone" });
        const v = p.state === "denied" ? "blocked" : p.state === "granted" ? "ok" : "unknown";
        setMic(v);
        p.onchange = () => setMic(p.state === "denied" ? "blocked" : p.state === "granted" ? "ok" : "unknown");
        return v;
      }
    } catch { /* not every browser answers the permissions query */ }
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
      setMic("ok"); return "ok";
    } catch (e) {
      const v = e?.name === "NotFoundError" ? "none" : "blocked";
      setMic(v); return v;
    }
  }, []);
  useEffect(() => { if (enabled) checkMic(false); }, [enabled, checkMic]);

  // ── Registration ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !extension || !password) return undefined;
    let ua = null, stopped = false;
    const lines = linesRef.current;
    import("jssip").then((mod) => {
      if (stopped) return;
      const JsSIP = mod.default || mod;
      JsSIP.debug.disable("JsSIP:*");
      ua = new JsSIP.UA({
        sockets: [new JsSIP.WebSocketInterface(SIP_WS)],
        uri: `sip:${extension}@${SIP_DOMAIN}`, password, display_name: extension, authorization_user: extension,
        registrar_server: `sip:${SIP_DOMAIN}`, register: true, session_timers: false, user_agent: "Teleredo/2.0",
      });
      ua.on("connecting",         () => setReg((r) => (r === "registered" ? "reconnecting" : "connecting")));
      ua.on("registered",         () => { sipLog("[phone] registered", extension); setReg("registered"); setRegError(""); });
      ua.on("unregistered",       () => setReg((r) => (r === "registered" ? "reconnecting" : r)));
      ua.on("registrationFailed", (e) => { setReg("failed"); setRegError(e?.cause || "Registration failed"); });
      ua.on("disconnected",       () => setReg("reconnecting"));
      ua.on("newRTCSession", ({ session }) => {
        if (session.direction !== "incoming") return;
        const busy = [...linesRef.current.values()];
        // One call ringing at a time, two calls at most (one talking, one on hold): another caller hears busy — the engine moves on
        // (the next agent, or the flow's next step).
        if (busy.some((l) => l.info.state === "ringing") || busy.length >= 2) {
          try { session.terminate({ status_code: 486, reason_phrase: "Busy Here" }); } catch { /* gone */ }
          return;
        }
        const id = `in-${++seq}`;
        const number = session.remote_identity?.uri?.user || "";
        const name = session.remote_identity?.display_name || "";
        linesRef.current.set(id, { session, stream: null, info: { id, dir: "in", number, name, state: "ringing", since: Date.now(), answeredAt: null, muted: false,
          waiting: busy.length > 0 } });
        watch(id, session);
        publish(); reportFocus();
        // A desktop notification when the tab is not in front.
        try {
          if (document.hidden && "Notification" in window && Notification.permission === "granted") {
            const n = new Notification(cbRef.current.notifyText?.title ?? "Incoming call", { body: name || number, tag: "teleredo-call", renotify: true });
            n.onclick = () => { window.focus(); n.close(); };
          }
        } catch { /* notifications unavailable */ }
      });
      setReg("connecting");
      ua.start();
      uaRef.current = ua;
      currentUa = ua;
    }).catch((err) => { setReg("failed"); setRegError("JsSIP not available: " + err.message); });
    return () => {
      stopped = true;
      for (const l of lines.values()) { try { l.session.terminate(); } catch { /* gone */ } }
      lines.clear();
      const u = uaRef.current;
      uaRef.current = null;
      if (currentUa === u) currentUa = null;
      if (u) {
        try { if (u.isRegistered()) u.unregister({ all: true }); } catch { /* closing */ }
        setTimeout(() => { try { u.stop(); } catch { /* stopped */ } }, 300);
      }
    };
  }, [enabled, extension, password, publish, reportFocus, watch]);

  // ── Tones ─────────────────────────────────────────────────────────────────
  const ringing = calls.some((c) => c.state === "ringing");
  const talking = calls.some((c) => c.state === "active" || c.state === "held" || c.state === "calling");
  useEffect(() => {
    if (!ringing) return undefined;
    return startTones(talking ? "waiting" : "ring");
  }, [ringing, talking]);

  // A page reload in a call would drop it.
  useEffect(() => {
    if (!calls.length) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [calls.length]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const holdOthers = useCallback((exceptId) => {
    for (const [id, l] of linesRef.current) {
      if (id !== exceptId && l.info.state === "active") { try { l.session.hold(); } catch (e) { console.warn("[phone] hold failed:", e); } }
    }
  }, []);

  const dial = useCallback((target, name = "") => {
    const ua = uaRef.current;
    const dest = String(target ?? "").replace(/[\s\-().]/g, "");
    if (!ua || reg !== "registered" || !dest) return false;
    holdOthers(null);
    const id = `out-${++seq}`;
    const session = ua.call(`sip:${dest}@${SIP_DOMAIN}`, { mediaConstraints: MEDIA, pcConfig: { iceServers: ICE } });
    linesRef.current.set(id, { session, stream: null, info: { id, dir: "out", number: dest, name, state: "calling", since: Date.now(), answeredAt: null, muted: false } });
    watch(id, session);
    publish(); reportFocus();
    return true;
  }, [reg, holdOthers, watch, publish, reportFocus]);

  const answer = useCallback(async (id) => {
    const l = linesRef.current.get(id);
    if (!l || l.info.state !== "ringing") return;
    const m = await checkMic(true);
    if (m !== "ok") return;
    holdOthers(id);
    try {
      l.session.answer({ mediaConstraints: MEDIA, pcConfig: { iceServers: ICE } });
      update(id, { state: "active", answeredAt: Date.now(), waiting: false });
    } catch (e) { console.error("[phone] answer failed:", e); }
  }, [checkMic, holdOthers, update]);

  const hangup = useCallback((id) => {
    const l = linesRef.current.get(id);
    if (!l) return;
    try { l.session.terminate(l.info.state === "ringing" ? { status_code: 486, reason_phrase: "Busy Here" } : undefined); }
    catch { drop(id, "ended"); }
  }, [drop]);

  const toggleMute = useCallback((id) => {
    const l = linesRef.current.get(id);
    if (!l) return;
    try { if (l.info.muted) l.session.unmute({ audio: true }); else l.session.mute({ audio: true }); update(id, { muted: !l.info.muted }); }
    catch (e) { console.warn("[phone] mute failed:", e); }
  }, [update]);

  const hold = useCallback((id) => { const l = linesRef.current.get(id); if (l?.info.state === "active") { try { l.session.hold(); } catch (e) { console.warn(e); } } }, []);
  const resume = useCallback((id) => {
    const l = linesRef.current.get(id);
    if (!l || l.info.state !== "held") return;
    holdOthers(id);
    try { l.session.unhold(); } catch (e) { console.warn("[phone] resume failed:", e); }
  }, [holdOthers]);

  // Keypad tones on the call being talked on: RFC 4733 through the browser, else JsSIP's.
  const sendDtmf = useCallback((key) => {
    const l = [...linesRef.current.values()].find((x) => x.info.state === "active" || x.info.state === "calling");
    if (!l) return;
    try {
      const sender = l.session.connection?.getSenders?.().find((s) => s.track?.kind === "audio")?.dtmf;
      if (sender?.canInsertDTMF) { sender.insertDTMF(key, 160, 50); return; }
    } catch { /* the fallback below */ }
    try { if (l.info.state === "active") l.session.sendDTMF(key, { duration: 160, interToneGap: 50 }); }
    catch (e) { console.warn("[phone] DTMF failed:", e?.message); }
  }, []);

  const retry = useCallback(() => {
    const ua = uaRef.current;
    if (!ua) return;
    try { if (ua.isConnected()) ua.register(); else { ua.stop(); setTimeout(() => ua.start(), 300); } } catch (e) { console.warn("[phone] retry failed:", e); }
  }, []);

  return { reg, regError, mic, calls, audioRef, dial, answer, hangup, toggleMute, hold, resume, sendDtmf, checkMic, retry };
}
